import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Camera, ImagePlus, Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DYLAN_SEEDS = [
  "Lily", "Nolan", "Felix", "Aneka", "Bella", "Chase", "Mia", "Leo",
  "Zara", "Jude", "Luna", "Max", "Chloe", "Kai", "Aria", "Owen",
  "Sophie", "Ravi", "Emma", "Liam", "Olive", "Bear", "Daisy", "Finn",
];

function getDylanUrl(seed: string) {
  return `https://api.dicebear.com/9.x/dylan/svg?seed=${encodeURIComponent(seed)}`;
}

interface AvatarPickerProps {
  currentUrl: string;
  uploading: boolean;
  onSelectPreset: (url: string) => void;
  onUploadFile: (file: File) => void;
}

const AvatarPicker = ({ currentUrl, uploading, onSelectPreset, onUploadFile }: AvatarPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadFile(file);
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted ring-2 ring-border">
              <img src={currentUrl} alt="Avatar" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera size={20} className="text-white" />
            </div>
          </button>
        </DialogTrigger>

        <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {t("profile.chooseAvatar", { defaultValue: "Choose Avatar" })}
            </DialogTitle>
          </DialogHeader>

          {/* Upload option */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-3 w-full p-3 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-colors"
          >
            {uploading ? (
              <Loader2 size={18} className="text-muted-foreground animate-spin" />
            ) : (
              <ImagePlus size={18} className="text-muted-foreground" />
            )}
            <span className="text-sm text-foreground">
              {t("profile.uploadPhoto", { defaultValue: "Upload from gallery" })}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />

          {/* Preset grid */}
          <div>
            <p className="text-xs text-muted-foreground mb-3">
              {t("profile.orChoosePreset", { defaultValue: "Or choose an avatar" })}
            </p>
            <div className="grid grid-cols-5 gap-2">
              {DYLAN_SEEDS.map((seed) => {
                const url = getDylanUrl(seed);
                const isSelected = currentUrl === url;
                return (
                  <button
                    key={seed}
                    onClick={() => { onSelectPreset(url); setOpen(false); }}
                    className={cn(
                      "relative w-full aspect-square rounded-full overflow-hidden bg-muted ring-2 transition-all hover:scale-110",
                      isSelected ? "ring-primary ring-offset-2 ring-offset-background" : "ring-transparent hover:ring-border"
                    )}
                  >
                    <img src={url} alt={seed} className="w-full h-full object-cover" loading="lazy" />
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <Check size={16} className="text-primary" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => setOpen(false)}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
        </DialogContent>
      </Dialog>

      <button
        onClick={() => setOpen(true)}
        className="text-xs text-primary hover:underline"
      >
        {t("profile.changePhoto", { defaultValue: "Change photo" })}
      </button>
    </div>
  );
};

export default AvatarPicker;
